import { convertMeasure } from '@e2m-customer-portal/shared';
import { addMinutes, eachDayOfInterval, eachMinuteOfInterval, getHours, getTime, toDate } from 'date-fns';
import { DetectBrowserDevice } from 'domains/forecast/utils';
import { EChartsOption, MarkLineComponentOption, SeriesOption } from 'echarts';
import { Device, DeviceTimeSeries, FeedInValue, ForecastSlice, Interval, Maybe } from 'generated/graphql';
import { AxisLabelsFormatterCallbackFunction, Point } from 'highcharts';
import { TFunction } from 'i18next';
import { SERIES_IDS } from '../constants';

export const POSITIVE_BAND_AXIS = 'positive-band';
export const NEGATIVE_BAND_AXIS = 'negative-band';

export namespace ChartColor {
  export const Name = [
    'Bubblegum Transit',
    'Velvet Violet',
    'Dirty Jeans',
    'Burgundy Bourgeoisie',
    'Dreamy Dentist',
    'Mint Chocolate',
  ];

  /**
   * Full strength chart colors
   */
  export const PrimaryFull = ['#1B8FF8', '#6736A8', '#A8562E', '#4B3F9E', '#16324F', '#9C7A2E'];
  export const SecondaryFull = ['#E92E60', '#1B8FF8', '#1857C4', '#B23E7A', '#0E8FA3', '#6B2331'];
  export const TertiaryFull = ['#6736A8', '#E92E60', '#33414D', '#1B6B75', '#5C1F45', '#1F5E63'];

  /**
   * Chart colors with '60% opacity'
   */
  export const Primary60 = ['#76BCFB', '#A486CB', '#CB9A82', '#938CC5', '#738495', '#C4AF82'];
  export const Secondary60 = ['#F282A0', '#76BCFB', '#749ADC', '#D18BAF', '#6EBCC8', '#A67B83'];
  export const Tertiary60 = ['#A486CB', '#F282A0', '#858D94', '#76A6AC', '#9D798F', '#799EA1'];

  /**
   * Chart colors with '30% opacity'
   */
  export const Primary30 = ['#BBDDFD', '#D1C3E5', '#E5CCC0', '#C9C5E2', '#B9C2CA', '#E1D7C0'];
  export const Secondary30 = ['#F8C0CF', '#BBDDFD', '#BACDED', '#E8C5D7', '#B7DDE3', '#D3BDC1'];
  export const Tertiary30 = ['#D1C3E5', '#F8C0CF', '#C2C6CA', '#BBD3D6', '#CEBCC7', '#BCCFD0'];
}

export type BatteryPowerType = {
  power_charge: number;
  power_discharge: number;
};

export function formatTooltip(
  marketLocationName: string,
  t: TFunction,
  locale: string,
  data?: FeedInValue[],
  linkedData?: Maybe<FeedInValue[]>,
) {
  return function (this: Highcharts.TooltipFormatterContextObject) {
    const date = new Date(this.x as number);
    const points = [
      this.points?.find((p) => p.series.userOptions.id === SERIES_IDS.forecast)?.point,
      this.points?.find((p) => p.series.userOptions.id === SERIES_IDS.linkedForecast)?.point,
      this.points?.find((p) => p.series.userOptions.id === SERIES_IDS.measurement)?.point,
      this.points?.find((p) => p.series.userOptions.id === SERIES_IDS.linkedMeasurement)?.point,
      this.points?.find((p) => p.series.userOptions.id === SERIES_IDS.quality)?.point,
      this.points?.find((p) => p.series.userOptions.id === SERIES_IDS.linkedQuality)?.point,
      this.points?.find((p) => p.series.userOptions.id === SERIES_IDS.spotPrice)?.point,
    ];

    return `<div class="w-[400px] text-wrap bg-white p-4 shadow-md">
      <p class="my-0 text-sm leading-normal font-semibold text-black">${marketLocationName}</p>
      <p class="my-1 text-grey-400">${t('MARKET_LOCATION_FEED_IN_POPUP_DATE', { date })}</p>
      ${points.map((point) => getTooltipValue(point, t, locale, data, linkedData)).join('')}
    </div>`;
  };
}

function getTooltipValue(
  point: Point | undefined,
  t: TFunction,
  locale: string,
  data?: FeedInValue[],
  linkedData?: Maybe<FeedInValue[]>,
  unit = 'kW',
) {
  switch (point?.series.userOptions.id) {
    case SERIES_IDS.spotPrice:
      return `<p class="flex items-start justify-between ml-4 -indent-4"><span><span class="text-chart-spotPrices">&#x25AC</span> ${point.series.name}:</span><span class="text-right font-semibold text-chart-spotPrices">${point.y! < 0 ? '-' : ''}${Math.abs(point.y!)?.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/MWh</span></p>`;
    case SERIES_IDS.forecast:
      return `<p class="flex items-start justify-between ml-4 -indent-4"><span><span class="text-chart-forecast">&#x25AC</span> ${point.series.name}:</span><span class="text-right font-semibold text-chart-forecast">${Math.round(point.y as number)} ${unit}</span></p>`;
    case SERIES_IDS.measurement:
      return `<p class="flex items-start justify-between ml-4 -indent-4"><span><span class="text-chart-measurement">&#x25AC</span> ${point.series.name}:</span><span class="text-right font-semibold text-chart-measurement">${Math.round(point.y as number)} ${unit}</span></p>`;
    case SERIES_IDS.linkedMeasurement:
      return `<p class="flex items-start justify-between ml-4 -indent-4"><span><span class="text-chart-measurement">&#x25AC</span> ${point.series.name}:</span><span class="text-right font-semibold text-chart-measurement">${Math.round(point.y as number)} ${unit}</span></p>`;
    case SERIES_IDS.quality:
      return formatQualityValue(point, t, data);
    case SERIES_IDS.linkedQuality:
      return formatQualityValue(point, t, linkedData);
    default:
      return;
  }
}

function formatQualityValue(point: Point, t: TFunction, data?: Maybe<FeedInValue[]>) {
  const code = data?.find((value) => point.x === getTime(value.date))?.msconsCode ?? undefined;
  return `<p class="flex items-start justify-between ml-4 -indent-4">
    <span><span class="text-chart-mscons-${code && ['220', 'Z18', '67'].includes(code) ? code : 'missing'}">&#x25AC</span> ${point.series.name}:</span>
    <span class="hidden md:inline text-right text-chart-mscons-${code && ['220', 'Z18', '67'].includes(code) ? code : 'missing'}">
      ${!code || ['220', 'Z18', '67'].includes(code) ? t(code ? `MARKET_LOCATION_FEED_IN_MSCONS_CODE_${code as '220' | 'Z18' | '67'}` : 'MARKET_LOCATION_FEED_IN_MSCONS_CODE_OTHER', { code }) : `[${code}]`}
    </span>
    <span class="md:hidden text-right text-chart-mscons-${code && ['220', 'Z18', '67'].includes(code) ? code : 'missing'}">
      ${code}
    </span>
  </p>`;
}

export function dateAxisFormatter(t: TFunction): AxisLabelsFormatterCallbackFunction {
  return ({ value }) => {
    const date = toDate(value as number);
    if (getHours(date) === 0) {
      return `<span id=timestamp-${value} style='font-weight:bold;color:"#000";scroll-margin-left:55px'>${t('FORECAST_LINE_CHART_DATE_AXIS_DAY', { date })}</span>`;
    }
    return t('FORECAST_LINE_CHART_DATE_AXIS_HOUR', { date });
  };
}

export function unitFormatter(unit: string, locale?: string): AxisLabelsFormatterCallbackFunction {
  return ({ value }) =>
    `${
      // value can and will be number, but Highcharts types do not reflect that properly
      typeof value === 'number' ? value.toLocaleString(locale, { maximumFractionDigits: 3 }) : value
    }\u202f${unit}`;
}

export function tooltipPositioner(
  this: Highcharts.Tooltip,
  labelWidth: number,
  labelHeight: number,
  point: Highcharts.TooltipPositionerPointObject,
) {
  return {
    x:
      point.plotX + labelWidth > this.chart.plotWidth
        ? point.plotX + this.chart.plotLeft - labelWidth - 20
        : point.plotX + this.chart.plotLeft + 20,
    y: 0,
  };
}

export const getBatteryPowerLinesOption = (
  t: TFunction<'translation', undefined>,
  dataInterval: Interval,
  power?: BatteryPowerType,
  includeMarkLine?: boolean,
): SeriesOption[] | undefined => {
  if (!power) return;
  else if (power.power_charge !== 0 || power.power_discharge !== 0) {
    const chargePower = convertMeasure({ value: power.power_charge, unit: 'MW' }, 'kW').value;
    const dischargePower = convertMeasure({ value: power.power_discharge, unit: 'MW' }, 'kW').value;

    const series: SeriesOption = {
      type: 'line',
      lineStyle: {
        width: 0,
      },
      tooltip: {
        show: false,
      },
      symbol: 'none',
      markLine: includeMarkLine
        ? {
            symbol: 'none',
            silent: true,
            z: 15,
            animation: false,
          }
        : undefined,
    };

    return [
      {
        ...series,
        data: eachMinuteOfInterval(dataInterval, { step: 15 }).map((date) => [date.getTime(), chargePower]),
        stack: NEGATIVE_BAND_AXIS,
        markLine: includeMarkLine
          ? {
              ...series.markLine,
              data: [
                chargePower !== 0
                  ? {
                      yAxis: chargePower,
                      symbol: 'none',
                      label: {
                        formatter: `${t('COLOCATION_OVERVIEW_CHARTS_POWER_CHARGE', { value: chargePower })}`,
                        position: 'insideEndTop',
                        textShadowColor: '#F9F9F9',
                        textShadowBlur: 0,
                        padding: 1,
                      },
                      lineStyle: {
                        cap: 'butt',
                        color: '#424242',
                        type: 'dashed',
                      },
                    }
                  : {},
              ],
            }
          : undefined,
      } as SeriesOption,
      {
        ...series,
        data: eachMinuteOfInterval(dataInterval, { step: 15 }).map((date) => [date.getTime(), dischargePower]),
        stack: POSITIVE_BAND_AXIS,
        markLine: includeMarkLine
          ? {
              ...series.markLine,
              data: [
                dischargePower !== 0
                  ? {
                      yAxis: dischargePower,
                      symbol: 'none',
                      label: {
                        formatter: `${t('COLOCATION_OVERVIEW_CHARTS_POWER_DISCHARGE', { value: dischargePower })}`,
                        position: 'insideEndBottom',
                        textShadowColor: '#F9F9F9',
                        textShadowBlur: 0,
                        padding: 1,
                      },
                      lineStyle: {
                        cap: 'butt',
                        color: '#424242',
                        type: 'dashed',
                      },
                    }
                  : {},
              ],
            }
          : undefined,
      } as SeriesOption,
    ];
  }
};

export function defaultEChartsOptionForInterval(t: TFunction, interval: Interval, title?: string): EChartsOption {
  const isMobile = DetectBrowserDevice() === 'Mobile';
  const chartInsets = {
    top: 22,
    right: isMobile ? 10 : 134,
    bottom: 65,
    left: isMobile ? 80 : 112,
  };

  return {
    grid: {
      ...chartInsets,
      show: true,
      backgroundColor: '#F9F9F9',
      borderWidth: 0,
    },
    title: {
      show: !!title,
      text: title,
      left: chartInsets.left,
      top: 10,
    },
    toolbox: {
      show: true,
      feature: {
        dataZoom: {
          icon: undefined,
          yAxisIndex: 'none',
          brushStyle: {},
        },
      },
    },
    dataZoom: [
      {
        type: 'slider',
        filterMode: 'none',
        right: isMobile ? 10 : 135,
        bottom: 10,
        left: isMobile ? 100 : 109,
        xAxisIndex: 'all',
        labelFormatter: (value) => t('MARKET_LOCATION_FEED_IN_POPUP_DATE', { date: toDate(value) }),
      },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        label: {
          formatter: (params) => t('MARKET_LOCATION_FEED_IN_POPUP_DATE', { date: toDate(params.value) }),
        },
      },
    },
    legend: {
      show: !isMobile,
      orient: 'horizontal',
      top: 0,
      right: chartInsets.right,
    },
    xAxis: {
      type: 'time',
      min: interval.start.getTime(),
      max: interval.end.getTime(),
      splitNumber: eachDayOfInterval(interval).length,
      minorTick: {
        splitNumber: 6,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#F0F0F0',
          type: 'dashed',
        },
      },
      minorSplitLine: {
        show: false,
        lineStyle: {
          type: 'dashed',
        },
      },
      axisLabel: {
        interval: 0,
        rotate: 0,
        formatter(value) {
          const date = toDate(value);
          if (date.getHours()) return `{hour|${t('FORECAST_LINE_CHART_DATE_AXIS_HOUR', { date })}}`;
          return `{date|${t('FORECAST_LINE_CHART_DATE_AXIS_DAY', { date })}}`;
        },
        rich: {
          date: {
            fontWeight: 'bolder',
          },
          hour: {
            fontWeight: 'lighter',
          },
        },
        color: '#888888',
        hideOverlap: true,
      },
      axisLine: {
        onZero: false,
        lineStyle: {
          cap: 'butt',
          color: '#D0D0D0',
        },
      },
    },
  };
}

export const nowMarkLineSeriesOption: SeriesOption = {
  type: 'line',
  markLine: {
    symbol: 'none',
    silent: true,
    data: [
      {
        xAxis: Date.now(),
        symbol: 'none',
        label: {
          show: false,
        },
        lineStyle: {
          cap: 'butt',
          color: '#424242',
          type: 'dashed',
        },
      },
    ],
  } satisfies MarkLineComponentOption,
};

export const getSortedDeviceTimeSeries = (device: Device, deviceTimeSeries?: readonly DeviceTimeSeries[]) => {
  const timeSeries =
    deviceTimeSeries?.find((timeSeries) => timeSeries.deviceNumber === device.internalId) ??
    ({ id: '', deviceNumber: device.internalId, datapoints: [] } satisfies DeviceTimeSeries);
  return [...timeSeries.datapoints].sort((a, b) => a.date.getTime() - b.date.getTime());
};

export const getOutagesData = (deviceSubmittedForecast: ForecastSlice[]) =>
  deviceSubmittedForecast.map((forecast) => (forecast.hasDowntime ? [forecast.date, 1] : null));

export const getOutages = (deviceSubmittedForecast: ForecastSlice[]) => {
  const outages: Array<{ interval: { start: number | Date; end: number | Date }; isOutage: boolean }> = [];
  for (const slice of deviceSubmittedForecast) {
    const outage = outages.findLast((o) => o.interval.start < slice.date);
    if (outage?.interval && outage.isOutage === slice.hasDowntime) {
      outage.interval.end = addMinutes(slice.date, 15);
    } else {
      outages.push({
        interval: { start: slice.date.getTime(), end: addMinutes(slice.date, 15).getTime() },
        isOutage: slice.hasDowntime,
      });
    }
  }
  return outages.filter((o) => o.isOutage);
};
